// Client mirror of server/api/contact/contactCategories.js.
//
// Keep both files identical. server/api/contact/contactCategories.test.js fails
// if they drift. The labels here are only used for the mail; what the customer
// sees comes from the `ContactForm.category.*` / `ContactForm.role.*` keys.

const ACCENT_CASE = '#0E3B43'; // petrol, see src/styles/mui/colors.js:80
const ACCENT_NEUTRAL = '#5A6B6F';

const ALL_ROLES = ['camper', 'host', 'prospect'];

// `roles` lists who may pick the category on the form. Two exclusions, both
// about categories that cannot apply to the role:
//   - a host has no pass of his own to ask about (the pass is a camper product),
//   - a prospect has no account and therefore no booking, payment or refund.
// This is a UI rule only. The server keeps accepting every category from every
// role: legacy Cordova builds send no role at all, and a request that reaches
// support with an odd-looking category is still a request worth answering.
export const CONTACT_CATEGORIES = [
  { key: 'booking', tag: 'BOOKING', label: 'Buchung', reference: 'booking', roles: ['camper', 'host'], accent: ACCENT_CASE },
  { key: 'payment', tag: 'PAYMENT', label: 'Zahlung & Rechnung', reference: 'booking', roles: ['camper', 'host'], accent: ACCENT_CASE },
  { key: 'cancellation', tag: 'CANCELLATION', label: 'Stornierung & Erstattung', reference: 'booking', roles: ['camper', 'host'], accent: ACCENT_CASE },
  { key: 'pass', tag: 'PASS', label: 'Jahrespass / 25Pass', reference: 'pass', roles: ['camper', 'prospect'], accent: ACCENT_CASE },
  { key: 'listing', tag: 'LISTING', label: 'Inserat & Gastgeber-Konto', reference: 'listing', roles: ALL_ROLES, accent: ACCENT_CASE },
  { key: 'account', tag: 'ACCOUNT', label: 'Konto & Login', reference: null, roles: ALL_ROLES, accent: ACCENT_NEUTRAL },
  { key: 'technical', tag: 'TECHNICAL', label: 'Technisches Problem', reference: null, roles: ALL_ROLES, accent: ACCENT_NEUTRAL },
  { key: 'other', tag: 'OTHER', label: 'Sonstiges', reference: null, roles: ALL_ROLES, accent: ACCENT_NEUTRAL },
];

export const CONTACT_ROLES = [
  { key: 'camper', label: 'Camper' },
  { key: 'host', label: 'Gastgeber' },
  { key: 'prospect', label: 'Interessent' },
];

// Legacy Cordova payloads predate the role field entirely. An absent role
// gets this fallback instead of silently reading as "Interessent" — it might
// still carry a verified session user id. Deliberately not part of
// CONTACT_ROLES: it must never appear as a fourth option on the form's radio
// group, and findRole() must never resolve it from client-supplied input.
export const FALLBACK_ROLE = { key: 'unknown', label: 'Unbekannt' };

export const CONTACT_CATEGORY_KEYS = CONTACT_CATEGORIES.map((category) => category.key);
export const CONTACT_ROLE_KEYS = CONTACT_ROLES.map((role) => role.key);

export const findCategory = (key) =>
  CONTACT_CATEGORIES.find((category) => category.key === key) || null;

export const findRole = (key) => CONTACT_ROLES.find((role) => role.key === key) || null;

/**
 * The categories a given role may pick from.
 *
 * An unknown role (the legacy fallback, or nothing selected yet) gets the full
 * list rather than an empty dropdown — hiding everything would be a worse
 * failure mode than showing a category that does not quite fit.
 *
 * @param {string} roleKey
 * @returns {typeof CONTACT_CATEGORIES}
 */
export const categoriesForRole = (roleKey) =>
  CONTACT_ROLE_KEYS.includes(roleKey)
    ? CONTACT_CATEGORIES.filter((category) => category.roles.includes(roleKey))
    : CONTACT_CATEGORIES;

/** Whether `categoryKey` is offered to `roleKey` on the form. */
export const isCategoryAllowedForRole = (categoryKey, roleKey) =>
  categoriesForRole(roleKey).some((category) => category.key === categoryKey);
